import React, { useState } from 'react';

import { Icon, Select, TextField, TextFieldType } from '~/components/core';

// Options that can be selected for a single filter
export type FilterOption = { label: string; value: string };

export interface Filter<FilterKey extends string> {
  defaultLabel: string;
  filterKey: FilterKey; // e.g. FilesFilter.FILE_TYPE
  options: FilterOption[];
  placeholder: string;
  prefix?: string;
  searchable?: boolean;
}

// FiltersValues are the values of ALL the filters. E.g. { FilesFilter.FILE_TYPE: 'csv' }
export type FiltersValues<K extends string> = Record<K, string | undefined>;

export interface FiltersProps<FilterKey extends string> {
  filters: Filter<FilterKey>[];
  filtersValues: FiltersValues<FilterKey>;
  searchValue: string;
  setFilters: (newFilterValues: FiltersValues<FilterKey>) => void;
  children?: React.ReactNode;
  searchPlaceholder?: string;
  setSearchValue?: (search: string) => void;
}

/**
 * K - the type of
 */
const Filters = <FilterKey extends string>({
  filters,
  searchValue,
  filtersValues,
  setFilters,
  setSearchValue,
  searchPlaceholder,
  children
}: FiltersProps<FilterKey>) => {
  const [innerValue, setInnerValue] = useState(searchValue);

  const handleSetPartialFilter = (
    filterKey: FilterKey,
    newValue: string | number | undefined
  ) =>
    setFilters({
      ...filtersValues,
      [filterKey]: newValue
    });

  const handleSearchChange = (searchText: string) => {
    setSearchValue?.(searchText.trim());
    setInnerValue(searchText);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-3"
      data-testid="filters-container"
    >
      {setSearchValue && (
        <TextField
          endIcon={Icon.SEARCH}
          onChange={handleSearchChange}
          placeholder={searchPlaceholder || 'SEARCH'}
          type={TextFieldType.DEFAULT}
          value={innerValue}
        />
      )}
      <div
        className="flex grow flex-row space-x-2"
        data-testid="filters-select-container"
      >
        {filters.map(
          ({
            defaultLabel,
            options,
            placeholder,
            filterKey,
            prefix,
            searchable
          }) => {
            const defaultOption: FilterOption | undefined = defaultLabel
              ? { label: defaultLabel, value: '' }
              : undefined;

            const optionsWithDefault = defaultOption
              ? [defaultOption, ...options]
              : options;

            return (
              <Select
                key={filterKey}
                onChange={(value) =>
                  handleSetPartialFilter(filterKey, value ?? undefined)
                }
                options={optionsWithDefault}
                placeholderLabel={placeholder}
                prefix={prefix || ''}
                searchable={searchable}
                value={filtersValues[filterKey] ?? ''}
              />
            );
          }
        )}
        {children}
      </div>
    </div>
  );
};

export default Filters;
